/**
 * DynamicTableRenderer — Table component driven by form schema columns.
 *
 * Renders a compact data table where columns are determined by form fields
 * that have DisplayInList=true, ordered by ListDisplayOrder.
 *
 * Usage:
 *   <DynamicTableRenderer
 *     columns={tableData.columns}
 *     rows={tableData.rows}
 *     totalCount={tableData.totalCount}
 *     page={tableData.page}
 *     pageSize={tableData.pageSize}
 *     onPageChange={setPage}
 *     onRowClick={handleRowClick}
 *   />
 */

import { useState, useMemo, memo } from 'react';
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import type {
  DynamicTableRendererProps,
  DynamicField,
  DynamicTableSortState,
  DynamicTableFilterValue,
} from '../../types/dynamicEntity';
import { DynamicTableHeader } from './DynamicTableHeader';
import { DynamicFilterBar } from './DynamicFilterBar';
import { DynamicFilterDrawer } from './DynamicFilterDrawer';
import { DynamicColumnPicker } from './DynamicColumnPicker';
import { GridViewsDropdown } from '../../components/ui/GridViewsDropdown';
import { AppInlineLoader } from '../../components/ui/AppLoading';

// ─── Cell formatters ───────────────────────────────────────────

function formatCell(field: DynamicField, raw: unknown): React.ReactNode {
  if (raw == null || raw === '') return <span className="text-muted-foreground">—</span>;

  const str = String(raw);

  switch (field.fieldType) {
    case 'DATE':
      try {
        return new Date(str).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      } catch {
        return str;
      }

    case 'CHECKBOX':
      return str === 'true' || str === '1' ? 'Yes' : 'No';

    case 'DROPDOWN':
    case 'RADIO': {
      const match = field.options?.find((o) => o.value === str || o.label === str);
      return match?.label ?? str;
    }

    case 'MULTISELECT':
      try {
        const arr: string[] = typeof raw === 'string' ? JSON.parse(raw) : (raw as string[]);
        return arr
          .map((v) => field.options?.find((o) => o.value === v)?.label ?? v)
          .join(', ');
      } catch {
        return str;
      }

    default:
      return str.length > 60 ? `${str.slice(0, 57)}…` : str;
  }
}

// ─── Status badge helper ───────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const normalized = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  const colorMap: Record<string, string> = {
    active: 'bg-success-soft text-success border-success/30',
    inactive: 'bg-warning-soft text-warning border-warning/30',
    draft: 'bg-primary-soft text-primary border-primary/30',
    pending: 'bg-warning-soft text-warning border-warning/30',
    completed: 'bg-success-soft text-success border-success/30',
    rejected: 'bg-destructive/10 text-destructive border-destructive/30',
    archived: 'bg-info-soft text-info border-info/30',
  };
  const cls = colorMap[status.toLowerCase()] ?? 'bg-info-soft text-info border-info/30';
  return (
    <Badge variant="outline" className={`text-[11px] ${cls}`}>
      {normalized}
    </Badge>
  );
}

// ─── Memoized Row ──────────────────────────────────────────────

interface DynamicTableRowProps {
  row: Record<string, unknown>;
  columns: DynamicField[];
  onRowClick?: (row: Record<string, unknown>) => void;
}

const DynamicTableRow = memo(function DynamicTableRow({
  row,
  columns,
  onRowClick,
}: DynamicTableRowProps) {
  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
      {columns.map((col) => (
        <td
          key={col.fieldId}
          className="px-3 py-2 text-[12px] text-foreground whitespace-nowrap"
        >
          {formatCell(col, row[col.internalName])}
        </td>
      ))}
      <td className="px-3 py-2">
        {row._status ? (
          <StatusBadge status={String(row._status)} />
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      {onRowClick && (
        <td className="px-3 py-2">
          <button
            type="button"
            onClick={() => onRowClick(row)}
            className="p-1 hover:bg-muted rounded"
            title="View"
          >
            <Eye className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </td>
      )}
    </tr>
  );
});

// ─── Component ─────────────────────────────────────────────────

export function DynamicTableRenderer({
  columns,
  rows,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onRowClick,
  isLoading,
  className,
  sort: sortProp,
  onSortChange,
  filters: filtersProp,
  onFiltersChange,
  visibleColumnKeys: visibleKeysProp,
  onVisibleColumnsChange,
  showFilterBar = false,
  showColumnPicker = false,
  hiddenColumnKeys,
  savedViews,
  onApplySavedView,
  onSaveView,
  onDeleteView,
  onSetDefaultView,
}: DynamicTableRendererProps) {
  // ── Columns after UI-visibility rule exclusion ─────────────
  const eligibleColumns = useMemo(
    () =>
      hiddenColumnKeys && hiddenColumnKeys.size > 0
        ? columns.filter((c) => !hiddenColumnKeys.has(c.internalName))
        : columns,
    [columns, hiddenColumnKeys],
  );

  // ── Local state for uncontrolled mode ──────────────────────
  const [localSort, setLocalSort] = useState<DynamicTableSortState | null>(null);
  const [localFilters, setLocalFilters] = useState<DynamicTableFilterValue[]>([]);
  const [localVisibleKeys, setLocalVisibleKeys] = useState<string[]>(
    () => eligibleColumns.map((c) => c.internalName),
  );
  const [localSearch, setLocalSearch] = useState('');

  const sort = onSortChange ? sortProp : localSort;
  const handleSortChange = onSortChange ?? setLocalSort;

  const filters = onFiltersChange ? (filtersProp ?? []) : localFilters;
  const handleFiltersChange = onFiltersChange ?? setLocalFilters;

  const visibleKeys = onVisibleColumnsChange
    ? (visibleKeysProp ?? eligibleColumns.map((c) => c.internalName))
    : localVisibleKeys;
  const handleVisibleColumnsChange = onVisibleColumnsChange ?? setLocalVisibleKeys;

  // ── Filtered visible columns ───────────────────────────────
  const visibleColumns = useMemo(
    () => eligibleColumns.filter((c) => visibleKeys.includes(c.internalName)),
    [eligibleColumns, visibleKeys],
  );

  const totalPages = Math.ceil(totalCount / pageSize);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <AppInlineLoader label="Loading content" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-[12px] text-muted-foreground">No records found.</p>
      </div>
    );
  }

  const hasSavedViews = !!(savedViews && onApplySavedView && onSaveView && onDeleteView && onSetDefaultView);

  return (
    <div className={className}>
      {/* Filter bar + column picker + saved views toolbar */}
      {(showFilterBar || showColumnPicker || hasSavedViews) && (
        <div className="flex items-center gap-2 flex-wrap mb-2">
          {showFilterBar && (
            <>
              <DynamicFilterBar
                columns={eligibleColumns}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                searchValue={localSearch}
                onSearchChange={setLocalSearch}
              />
              <DynamicFilterDrawer
                columns={eligibleColumns}
                filters={filters}
                onFiltersChange={handleFiltersChange}
              />
            </>
          )}
          {showColumnPicker && (
            <DynamicColumnPicker
              columns={eligibleColumns}
              visibleColumnKeys={visibleKeys}
              onVisibleColumnsChange={handleVisibleColumnsChange}
            />
          )}
          {hasSavedViews && (
            <GridViewsDropdown
              views={savedViews}
              onApplyView={onApplySavedView}
              onSaveView={onSaveView}
              onDeleteView={onDeleteView}
              onSetDefault={onSetDefaultView}
            />
          )}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto border border-border rounded-lg">
        <table className="w-full text-left">
          <thead>
            <DynamicTableHeader
              columns={visibleColumns}
              sort={sort}
              onSortChange={handleSortChange}
              showStatusColumn
              showActionColumn={!!onRowClick}
            />
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <DynamicTableRow
                key={String(row._entityId ?? idx)}
                row={row}
                columns={visibleColumns}
                onRowClick={onRowClick}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && onPageChange && (
        <div className="flex items-center justify-between mt-2 px-1">
          <p className="text-[11px] text-muted-foreground">
            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of{' '}
            {totalCount}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              className="p-1 rounded hover:bg-muted disabled:opacity-40"
            >
              <ChevronLeft className="w-3.5 h-3.5 text-foreground" />
            </button>
            <span className="text-[11px] text-foreground px-1">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
              className="p-1 rounded hover:bg-muted disabled:opacity-40"
            >
              <ChevronRight className="w-3.5 h-3.5 text-foreground" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
